import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { matchPathname } from '../../util/routes';
import {
  CHAT_WIDGET_API_BASE,
  CHAT_WIDGET_SRC,
  CHAT_WIDGET_TENANT_KEY,
  showChatWidgetOnRoute,
} from '../../config/configChatWidget';

// The element the widget builds itself into, and the tag we inject to start
// it. Both are looked up by these rather than kept in React state, because the
// widget owns the container and React must not try to reconcile it.
const CONTAINER_ID = 'konversai-widget-container';
const SCRIPT_ID = 'konversai-widget-loader';

/** Remove the widget from the page entirely, not merely hide it. */
const unmountWidget = () => {
  document.getElementById(CONTAINER_ID)?.remove();
  document.getElementById(SCRIPT_ID)?.remove();
};

/**
 * Konvers AI chat bubble, mounted per route.
 *
 * Renders nothing itself: the widget script builds its own DOM under
 * `document.body`. This component only decides whether it should be there.
 *
 * Client-only. On the server this effect never runs, so the widget is absent
 * from the server-rendered HTML and appears once React has hydrated, which is
 * what the vendor's `async` script expects anyway.
 *
 * Injected as a real <script> element because the vendor's loader reads
 * `document.currentScript` to find its own data attributes; that is only set
 * for a genuine script tag, so the options cannot be passed any other way.
 *
 * Deliberately NOT marked `data-cookieconsent="ignore"`. The widget ships its
 * own Cookiebot-aware consent layer (consent.js): it keeps the chat session in
 * sessionStorage always, and only reaches for localStorage once the visitor
 * has accepted Preferences. Telling Cookiebot to ignore it would override that
 * for no gain.
 *
 * @component
 * @returns {null}
 */
const ChatWidget = () => {
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();

  const matched = matchPathname(location.pathname, routeConfiguration);
  const routeName = matched.length > 0 ? matched[0].route.name : null;
  const shouldShow = showChatWidgetOnRoute(routeName);

  useEffect(() => {
    if (!shouldShow) {
      unmountWidget();
      return undefined;
    }

    // The vendor's loader bails out early if its container already exists, so
    // re-injecting on every navigation within the allowed routes would be a
    // no-op — but it would still add a duplicate script tag. Skip it instead.
    if (document.getElementById(SCRIPT_ID)) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = CHAT_WIDGET_SRC;
    script.dataset.tenantKey = CHAT_WIDGET_TENANT_KEY;
    script.dataset.apiBase = CHAT_WIDGET_API_BASE;
    document.head.appendChild(script);

    return undefined;
  }, [shouldShow]);

  // Leave nothing behind if the whole app unmounts.
  useEffect(() => unmountWidget, []);

  return null;
};

export default ChatWidget;
